import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faUser } from "@fortawesome/free-solid-svg-icons";
import localforage from "localforage";
import { useLazyQuery, useMutation } from "@apollo/client";

import Button from "../../component/button/Button";
import "./LoginPage.scss";
import { validateEmail, validatePassword } from "../../service/utils/functions";
import {
  CHECK_PASSWORD_EXPIRED,
  CHECK_USED_EMAIL,
  FORGOT_PASSWORD_MUTATION,
  GET_USER_PAGE_PERMISSIONS,
  LOGIN_MUTATION,
  USER_MANAGEMENT_CHECK,
} from "../../service/graphql/auth";
import * as Storage from "../../service/storage";
import { setMatomoUser } from "../../service/matomo";

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginProgress, setLoginProgress] = useState(false);
  const [displaySignup, setDisplaySignup] = useState(false);

  const [invalidUsername, setInvalidUsername] = useState(false);
  const [alreadyUsedUsername, setAlreadyUsedUsername] = useState(false);
  const [registerFailureEmailMsg, setRegisterFailureEmailMsg] = useState("");
  const [resetPasswordFailureMsg, setResetPasswordFailureMsg] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [isBWEmail, setIsBWEmail] = useState(false);
  const [wrongPassword, setWrongPassword] = useState(false);
  const [unconfirmedPwd, setUnconfirmedPwd] = useState(false);
  const [expiredPwd, setExpiredPwd] = useState(false);
  const [genericError, setGenericError] = useState(false);

  const [displayResetPassword, setDisplayResetPassword] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  // State for live password validation error in signup form
  const [showPasswordRequirementsError, setShowPasswordRequirementsError] =
    useState(false);

  const [login, { data: loginRes, error: loginErr }] = useMutation(
    LOGIN_MUTATION,
    {
      errorPolicy: "all",
    }
  );
  const [checkUsedEmail] = useMutation(CHECK_USED_EMAIL, {
    errorPolicy: "all",
  });
  const [checkUserValidity] = useLazyQuery(USER_MANAGEMENT_CHECK, {
    errorPolicy: "all",
  });
  const [checkExpiredPassword] = useLazyQuery(CHECK_PASSWORD_EXPIRED);
  const [forgotPassword] = useMutation(FORGOT_PASSWORD_MUTATION, {
    onCompleted: (res) => {
      setLoginProgress(false);
      setRequestSent(true);
    },
    onError: (err) => {
      console.error(err);
      setLoginProgress(false);
    },
    errorPolicy: "all",
  });
  const [getUserPagePermissions] = useLazyQuery(GET_USER_PAGE_PERMISSIONS);

  const submitLogin = async () => {
    setLoginProgress(true);
    if (!validateEmail(username)) {
      setInvalidUsername(true);
      setLoginProgress(false);
      return;
    }
    const temp_auth_token = process.env.REACT_APP_CHECK_PWD_EXPIRATION_AUTHCODE;
    if (temp_auth_token) {
      await Storage.TOKEN.set(temp_auth_token);
      const checkList = await checkExpiredPassword({
        variables: { email: username },
        context: { apiName: "cms" },
      });
      const fetchDate =
        checkList.data?.userDetails?.data[0]?.attributes.password_expiration;
      if (fetchDate) {
        const currentDate = new Date();
        const formattedDate = new Date(fetchDate);
        if (currentDate.getTime() >= formattedDate.getTime()) {
          setExpiredPwd(true);
          setLoginProgress(false);
          return;
        } else
          login({
            variables: { identifier: username, password },
            context: { apiName: "cms" },
          });
      } else
        login({
          variables: { identifier: username, password },
          context: { apiName: "cms" },
        });
    } else setLoginProgress(false);
  };

  const submitSignup = async () => {
    resetErrorMsgs();
    setLoginProgress(true);

    if (username.length <= 0) {
      setInvalidUsername(true);
      setLoginProgress(false);
      return;
    }

    if (!validateEmail(email)) {
      setInvalidEmail(true);
      setLoginProgress(false);
      return;
    }

    if (email.endsWith("bywaters.com") || email.endsWith("bywaters.co.uk")) {
      setIsBWEmail(true);
      setLoginProgress(false);
      return;
    }

    const checkMail = await checkUsedEmail({
      variables: { email },
      context: { apiName: "cms" },
    });
    if (checkMail.data?.checkAlreadyUsedEmail) {
      setInvalidEmail(true);
      setRegisterFailureEmailMsg(
        "User account with this email is already registered in the system"
      );
      setLoginProgress(false);
      return;
    }

    // Live validation error state is also checked here to prevent submission
    if (!validatePassword(password)) {
      setShowPasswordRequirementsError(true);
      setLoginProgress(false);
      return;
    }

    if (password !== confirmPassword) {
      setUnconfirmedPwd(true);
      setLoginProgress(false);
      return;
    }

    const res = await checkUserValidity({
      variables: { email, username, password, action: "check" },
      context: { apiName: "middleware" },
    });
    switch (res.data?.checkUserValidity) {
      case "OK":
        setRequestSent(true);
        break;
      case "ALREADY_USED":
        setAlreadyUsedUsername(true);
        break;
      default:
        setGenericError(true);
    }
    setLoginProgress(false);
  };

  const submitResetRequest = async () => {
    setLoginProgress(true);
    if (validateEmail(username)) {
      if (
        !username.endsWith("bywaters.co.uk") &&
        !username.endsWith("bywaters.com")
      ) {
        forgotPassword({
          variables: { email: username },
          context: { apiName: "cms" },
        });
      } else {
        setResetPasswordFailureMsg(
          "Reset password is only available to BW clients. Please contact Bywaters IT team for assistance."
        );
        setInvalidUsername(true);
        setLoginProgress(false);
      }
    } else {
      setResetPasswordFailureMsg("Invalid Email");
      setInvalidUsername(true);
      setLoginProgress(false);
    }
  };

  useEffect(() => {
    if (loginRes && loginRes.login) {
      const jwt = loginRes.login.jwt;
      const u = loginRes.login.user;
      Storage.TOKEN.set(jwt);
      setMatomoUser(u.email || username, "Password");
      getUserPagePermissions({
        variables: { id: u.id },
        context: { apiName: "cms" },
      }).then((res) => {
        const permissionsTable =
          res.data?.userDetails?.data[0]?.attributes.page_permissions;
        localforage
          .setItem("userData", {
            name: u.username,
            email: username,
            page_permissions: permissionsTable,
          })
          .then(() => {
            window.history.replaceState(null, "", "/");
            navigate("/dashboard/home");
          });
      });
    }
  }, [loginRes, navigate, getUserPagePermissions, username, password]);

  useEffect(() => {
    if (loginErr && loginProgress) {
      if (
        username.endsWith("bywaters.com") ||
        username.endsWith("bywaters.co.uk")
      )
        submitAzureLogin();
      else {
        setWrongPassword(true);
        setLoginProgress(false);
      }
    }
  }, [loginErr, username, loginProgress]);

  const submitAzureLogin = () => {
    window.open(
      process.env.REACT_APP_BACKEND_LOCATION + "/api/connect/microsoft",
      "_self"
    );
  };

  const resetErrorMsgs = () => {
    setInvalidUsername(false);
    setAlreadyUsedUsername(false);
    setRegisterFailureEmailMsg("");
    setInvalidEmail(false);
    setIsBWEmail(false);
    setWrongPassword(false);
    setUnconfirmedPwd(false);
    setExpiredPwd(false);
    setRequestSent(false);
    setShowPasswordRequirementsError(false); // Reset live password error state
    setGenericError(false);
  };

  const renderLoginForm = () => {
    return (
      <>
        <div className="login-credential-block">
          <div className="login-inputbox">
            <label htmlFor="login_username">Email</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="email"
                  id="login_username"
                  name="username"
                  placeholder="Email"
                  value={username}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setUsername(e.target.value);
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faEnvelope} />
              </div>
              {invalidUsername && (
                <div className="error-msg">Invalid Username</div>
              )}
            </div>
          </div>
          <div className="login-inputbox">
            <label htmlFor="login_pwd">Password</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="password"
                  id="login_pwd"
                  name="pwd"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setPassword(e.target.value);
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faLock} />
              </div>
              {wrongPassword && !expiredPwd && (
                <div className="error-msg">Incorrect Password</div>
              )}
              {expiredPwd && (
                <div className="error-msg">
                  {
                    "Password is expired. Please reset it with the 'Forgot Password' option"
                  }
                </div>
              )}
            </div>
          </div>
          <Button
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#ffffff",
              color: "#0b0a31",
              width: "66%",
              height: "1.4rem",
              borderRadius: "1.4rem",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
            title="Login"
            loading={loginProgress}
            isLogin
            onClick={() => {
              resetErrorMsgs();
              submitLogin();
            }}
          />
          <div className="unshaped-selectors">
            <div
              className="reset-pwd"
              onClick={() => {
                resetFields();
                resetErrorMsgs();
                setDisplayResetPassword(true);
              }}
            >
              <p>Forgot password?</p>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderSignupForm = () => {
    return (
      <>
        <div className="login-credential-block">
          <div className="login-inputbox">
            <label htmlFor="signup_username">Display Username</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="text"
                  id="signup_username"
                  name="username"
                  placeholder="Display Username"
                  value={username}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setUsername(e.target.value);
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faUser} />
              </div>
              {invalidUsername && (
                <div className="error-msg">Empty username</div>
              )}
            </div>
          </div>
          <div className="login-inputbox">
            <label htmlFor="signup_email">Email</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="email"
                  id="signup_email"
                  name="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setEmail(e.target.value);
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faEnvelope} />
              </div>
              {invalidEmail &&
                !isBWEmail &&
                !alreadyUsedUsername &&
                (!registerFailureEmailMsg ||
                  registerFailureEmailMsg.length <= 0) && (
                  <div className="error-msg">Invalid email</div>
                )}
              {isBWEmail &&
                !alreadyUsedUsername &&
                (!registerFailureEmailMsg ||
                  registerFailureEmailMsg.length <= 0) && (
                  <div className="error-msg">
                    BW users are managed internally
                  </div>
                )}
              {alreadyUsedUsername &&
                (!registerFailureEmailMsg ||
                  registerFailureEmailMsg.length <= 0) && (
                  <div className="error-msg">
                    This email is not registered in the database yet. Please try
                    again later or contact your account manager.
                  </div>
                )}
              {registerFailureEmailMsg &&
                registerFailureEmailMsg.length > 0 && (
                  <div className="error-msg">{registerFailureEmailMsg}</div>
                )}
            </div>
          </div>
          <div className="login-inputbox">
            <label htmlFor="signup_pwd">Password</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="password"
                  id="signup_pwd"
                  name="pwd"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setPassword(e.target.value);
                    setShowPasswordRequirementsError(
                      e.target.value.length > 0 &&
                        !validatePassword(e.target.value)
                    );
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faLock} />
              </div>
              {showPasswordRequirementsError && (
                <div className="error-msg">
                  Password needs to be between 8 and 20 characters long and must
                  have at least one number and one symbol (@, $, !, %, *, #, ?,
                  &)
                </div>
              )}
              {genericError && (
                <div className="error-msg">An unexpected error occurred</div>
              )}
            </div>
          </div>
          <div className="login-inputbox">
            <label htmlFor="signup_confirm_pwd">Confirm Password</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="password"
                  id="signup_confirm_pwd"
                  name="pwd"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setConfirmPassword(e.target.value);
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faLock} />
              </div>
              {unconfirmedPwd && (
                <div className="error-msg">Passwords must match</div>
              )}
            </div>
          </div>
          <Button
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#ffffff",
              color: "#0b0a31",
              width: "66%",
              height: "1.4rem",
              borderRadius: "1.4rem",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
            title="Signup"
            loading={loginProgress}
            isLogin
            onClick={() => {
              resetErrorMsgs();
              submitSignup();
            }}
          />
          {requestSent && (
            <div className="confirm-msg">
              An email has been sent. Check out your mailbox!
            </div>
          )}
        </div>
      </>
    );
  };

  const renderForgotPasswordForm = () => {
    return (
      <>
        <h3 className="login-title">Reset Password</h3>
        <div className="login-credential-block">
          <div className="login-inputbox">
            <label htmlFor="resetpwd_username">Email</label>
            <div className="input-section-container">
              <div className="inputbox-container">
                <input
                  type="email"
                  id="resetpwd_username"
                  name="username"
                  placeholder="Email"
                  value={username}
                  onChange={(e) => {
                    resetErrorMsgs();
                    setUsername(e.target.value);
                  }}
                  required
                />
                <FontAwesomeIcon className="user-icon" icon={faEnvelope} />
              </div>
              {invalidUsername && (
                <div className="error-msg">{resetPasswordFailureMsg}</div>
              )}
            </div>
          </div>
          <Button
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#ffffff",
              color: "#0b0a31",
              width: "66%",
              height: "1.4rem",
              borderRadius: "1.4rem",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
            title="Send Request"
            loading={loginProgress}
            isLogin
            onClick={() => {
              resetErrorMsgs();
              submitResetRequest();
            }}
          />
          {requestSent && (
            <div className="confirm-msg">
              An email has been sent. Check out your mailbox!
            </div>
          )}
        </div>
      </>
    );
  };

  const resetFields = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  const switchForms = () => {
    resetFields();
    resetErrorMsgs();
    if (!displayResetPassword) setDisplaySignup(!displaySignup);
    setDisplayResetPassword(false);
  };

  return (
    <div className="screen-bg" style={{ backgroundColor: "#122048" }}>
      <video playsInline autoPlay muted loop>
        <source
          src="/images/video_background_login.mp4"
          type="video/mp4"
        ></source>
      </video>
      <div className="init-login-container">
        <div className="login-title-container">
          <div className="title-container">
            <h1>Welcome to B.R.A.D.</h1>
            <h3>Turn complex data into powerful business intelligence</h3>
          </div>
        </div>
        <div className="login-form">
          {displayResetPassword
            ? renderForgotPasswordForm()
            : displaySignup
            ? renderSignupForm()
            : renderLoginForm()}
          <div className="create-account-btn" onClick={() => switchForms()}>
            {displaySignup || displayResetPassword ? (
              <>Return to Login</>
            ) : (
              <>
                Don't have an account? <b>Register</b>
              </>
            )}
          </div>
        </div>
      </div>
      {/*
      <div className='light x1'></div>
      <div className='light x2'></div>
      <div className='light x3'></div>
      <div className='light x4'></div>
      <div className='light x5'></div>
      <div className='light x6'></div>
      <div className='light x7'></div>
      <div className='light x8'></div>
      <div className='light x9'></div>
      */}
    </div>
  );
};

export default LoginPage;
